import React, { useState } from 'react';
import { 
  Construction,
  Truck, 
  Package, 
  Clock, 
  DollarSign, 
  Shield, 
  Settings,
  Phone,
  Mail,
  MapPin,
  Star,
  CheckCircle,
  ArrowRight,
  Menu,
  X,
  PlayCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  companyInfo,
  services,
  whyChooseUs,
  testimonials,
  aboutContent,
  submitContactForm
} from '../data/mock';

const getIcon = (iconName) => {
  const icons = {
    Construction,
    Truck,
    Package,
    Clock,
    DollarSign,
    Shield,
    Settings
  };
  return icons[iconName];
};

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Demande envoyée !', {
          description: 'Nous avons bien reçu votre demande et vous répondrons dans les plus brefs délais.',
          style: {
            background: '#ffffff',
            color: '#404748',
            border: '1px solid #cad0d2'
          },
          descriptionClassName: 'text-gray-700'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: ''
        });
      } else {
        throw new Error(data.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur', {
        description: 'Une erreur est survenue lors de l\'envoi. Veuillez réessayer ou nous contacter directement.',
        style: {
          background: '#ffffff',
          color: '#dc2626',
          border: '1px solid #fecaca'
        },
        descriptionClassName: 'text-gray-700'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_manutention-express/artifacts/984tuqgm_1.png" 
                alt="ETGMOUCHEL Logo"
                className="h-12 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('services')} className="text-gray-600 hover:text-[#404748] font-medium transition-colors duration-200">
                Services
              </button>
              <button onClick={() => scrollToSection('about')} className="text-gray-600 hover:text-[#404748] font-medium transition-colors duration-200">
                À propos
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="text-gray-600 hover:text-[#404748] font-medium transition-colors duration-200">
                Témoignages
              </button>
              <Button 
                onClick={() => scrollToSection('contact')}
                className="bg-[#404748] hover:bg-[#2d3334] text-white font-semibold shadow-md"
              >
                Demander un devis
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-[#404748] p-2"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-100">
              <nav className="flex flex-col space-y-4">
                <button onClick={() => scrollToSection('services')} className="text-gray-600 hover:text-[#404748] font-medium transition-colors duration-200 text-left">
                  Services
                </button>
                <button onClick={() => scrollToSection('about')} className="text-gray-600 hover:text-[#404748] font-medium transition-colors duration-200 text-left">
                  À propos
                </button>
                <button onClick={() => scrollToSection('testimonials')} className="text-gray-600 hover:text-[#404748] font-medium transition-colors duration-200 text-left">
                  Témoignages
                </button>
                <Button 
                  onClick={() => scrollToSection('contact')}
                  className="bg-[#404748] hover:bg-[#2d3334] text-white font-semibold w-full"
                >
                  Demander un devis
                </Button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block mb-4 px-4 py-2 bg-[#cad0d2] rounded-full">
                <span className="text-[#404748] font-semibold text-sm">🚛 Disponible en Normandie</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#404748] mb-6 leading-tight">
                Service de Grutage & Transport
                <span className="block text-gray-700 mt-2">
                  Rapide et Fiable
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Entreprise spécialisée dans le levage, le porte-engins et le transport général. 
                Au service des professionnels et des particuliers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Button 
                  size="lg"
                  onClick={() => scrollToSection('contact')}
                  className="bg-[#404748] hover:bg-[#2d3334] text-white font-semibold text-lg px-8 py-6 shadow-lg group"
                >
                  Devis gratuit par mail
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => scrollToSection('services')}
                  className="border-2 border-[#404748] text-[#404748] hover:bg-[#404748] hover:text-white font-semibold text-lg px-8 py-6"
                >
                  Nos services
                </Button>
              </div>

              <div className="flex flex-wrap gap-8">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-[#cad0d2] rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[#404748]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Intervention</p>
                    <p className="text-lg font-semibold text-[#404748]">Rapide</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-[#cad0d2] rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-[#404748]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Prix</p>
                    <p className="text-lg font-semibold text-[#404748]">Compétitifs</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_manutention-express/artifacts/ee5mggr2_WhatsApp%20Image%202026-04-16%20at%2015.16.12.jpeg"
                  alt="Camion-grue ETGMOUCHEL"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#cad0d2] rounded-3xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 lg:py-32 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-[#404748] mb-4">
              Nos Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Un camion-grue polyvalent pour tous vos besoins
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = getIcon(service.icon);
              return (
                <Card 
                  key={service.id}
                  className="bg-white border-2 border-gray-100 hover:border-[#cad0d2] hover:shadow-xl transition-all duration-300 group"
                >
                  <CardHeader>
                    <div className="w-16 h-16 bg-gradient-to-br from-[#cad0d2] to-[#404748] rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl text-[#404748]">{service.title}</CardTitle>
                    <CardDescription className="text-gray-600 text-base">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="aspect-square rounded-xl overflow-hidden mb-4 shadow-md">
                      <img 
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-gray-700">
                          <CheckCircle className="w-5 h-5 text-[#404748] flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-[#404748] mb-4">
              Pourquoi Choisir ETGMOUCHEL ?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Les avantages qui font la différence
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((reason) => {
              const IconComponent = getIcon(reason.icon);
              return (
                <div 
                  key={reason.id}
                  className="text-center p-6 rounded-2xl bg-white border-2 border-gray-100 hover:border-[#cad0d2] hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="w-20 h-20 bg-[#cad0d2] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-[#404748] transition-colors duration-300">
                    <IconComponent className="w-10 h-10 text-[#404748] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-[#404748] mb-3">{reason.title}</h3>
                  <p className="text-gray-600">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 lg:py-32 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl md:text-5xl font-bold text-[#404748] mb-6">
                {aboutContent.title}
              </h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                {aboutContent.description}
              </p>
              
              <div className="space-y-4 mb-8">
                {aboutContent.values.map((value, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-[#404748] flex-shrink-0 mt-1" />
                    <p className="text-gray-700 text-lg">{value}</p>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-br from-[#cad0d2] to-gray-100 border-2 border-[#404748]/20 rounded-2xl p-6">
                <p className="text-gray-700 leading-relaxed">
                  {aboutContent.commitment}
                </p>
              </div>
            </div>

            <div className="relative order-1 lg:order-2">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_manutention-express/artifacts/3d5t46ul_WhatsApp%20Image%202026-04-17%20at%2011.19.16%20%281%29.jpeg"
                  alt="ETGMOUCHEL camion"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#cad0d2] rounded-3xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 lg:py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-[#404748] mb-4">
              Ce Que Disent Nos Clients
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              La satisfaction client au cœur de notre activité
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card 
                key={testimonial.id}
                className="bg-white border-2 border-gray-100 hover:border-[#cad0d2] hover:shadow-lg transition-all duration-300"
              >
                <CardHeader>
                  <div className="flex items-center space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#404748] text-[#404748]" />
                    ))}
                  </div>
                  <CardDescription className="text-gray-700 text-base leading-relaxed">
                    "{testimonial.content}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-[#404748] font-semibold">{testimonial.author}</p>
                    <p className="text-gray-600 text-sm">{testimonial.role}</p>
                    <p className="text-[#404748] text-sm font-medium">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 lg:py-32 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#404748] mb-4">
              Demandez Votre Devis Gratuit
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Remplissez le formulaire ou contactez-nous directement
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border-2 border-gray-100 shadow-lg">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#404748] font-medium">Nom complet *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="bg-white border-gray-300 text-[#404748] focus:border-[#404748] focus:ring-[#404748]"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-[#404748] font-medium">Entreprise (optionnel)</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="bg-white border-gray-300 text-[#404748] focus:border-[#404748] focus:ring-[#404748]"
                      placeholder="Nom de l'entreprise"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#404748] font-medium">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="bg-white border-gray-300 text-[#404748] focus:border-[#404748] focus:ring-[#404748]"
                      placeholder="votre@email.fr"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-[#404748] font-medium">Téléphone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="bg-white border-gray-300 text-[#404748] focus:border-[#404748] focus:ring-[#404748]"
                      placeholder="06 00 00 00 00"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service" className="text-[#404748] font-medium">Service demandé *</Label>
                  <Select value={formData.service} onValueChange={handleServiceChange} required>
                    <SelectTrigger className="bg-white border-gray-300 text-[#404748] focus:border-[#404748] focus:ring-[#404748]">
                      <SelectValue placeholder="Sélectionnez un service" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-300">
                      <SelectItem value="grutage" className="text-[#404748]">Grutage</SelectItem>
                      <SelectItem value="porte-engins" className="text-[#404748]">Porte-Engins</SelectItem>
                      <SelectItem value="transport" className="text-[#404748]">Transport Général</SelectItem>
                      <SelectItem value="autre" className="text-[#404748]">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[#404748] font-medium">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="bg-white border-gray-300 text-[#404748] focus:border-[#404748] focus:ring-[#404748] resize-none"
                    placeholder="Décrivez votre projet et vos besoins..."
                  />
                </div>

                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#404748] hover:bg-[#2d3334] text-white font-semibold text-lg py-6 shadow-lg"
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border-2 border-gray-100 shadow-lg">
                <h3 className="text-2xl font-bold text-[#404748] mb-6">Contactez-nous</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#cad0d2] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#404748]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Téléphone</p>
                      <a href={`tel:${companyInfo.phone1.replace(/\s/g, '')}`} className="text-[#404748] text-lg font-semibold hover:underline block">
                        {companyInfo.phone1}
                      </a>
                      <a href={`tel:${companyInfo.phone2.replace(/\s/g, '')}`} className="text-[#404748] text-lg font-semibold hover:underline block">
                        {companyInfo.phone2}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#cad0d2] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#404748]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Email</p>
                      <a href={`mailto:${companyInfo.email1}`} className="text-[#404748] hover:underline block break-all">
                        {companyInfo.email1}
                      </a>
                      <a href={`mailto:${companyInfo.email2}`} className="text-[#404748] hover:underline block break-all">
                        {companyInfo.email2}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#cad0d2] rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#404748]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Zone d'intervention</p>
                      <p className="text-[#404748] font-semibold">
                        Toute la Normandie
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#cad0d2] to-gray-100 rounded-3xl p-8 border-2 border-[#404748]/20">
                <h3 className="text-xl font-bold text-[#404748] mb-4">Horaires</h3>
                <div className="space-y-2 text-gray-700">
                  <p><span className="font-semibold text-[#404748]">Lundi - Vendredi:</span> 7h00 - 19h00</p>
                  <p><span className="font-semibold text-[#404748]">Samedi:</span> 8h00 - 18h00</p>
                  <p><span className="font-semibold text-[#404748]">Dimanche:</span> Sur demande</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-12">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_manutention-express/artifacts/984tuqgm_1.png" 
                  alt="ETGMOUCHEL Logo"
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-gray-600">
                {companyInfo.tagline}
              </p>
            </div>

            <div>
              <h4 className="text-[#404748] font-semibold mb-4">Liens Rapides</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-gray-600 hover:text-[#404748] transition-colors duration-200">
                    Services
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-gray-600 hover:text-[#404748] transition-colors duration-200">
                    À propos
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('testimonials')} className="text-gray-600 hover:text-[#404748] transition-colors duration-200">
                    Témoignages
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-gray-600 hover:text-[#404748] transition-colors duration-200">
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-[#404748] font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-[#404748]" />
                  <span>{companyInfo.phone1}</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-[#404748]" />
                  <span>{companyInfo.phone2}</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-[#404748]" />
                  <span className="break-all">{companyInfo.email1}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-8 text-center">
            <p className="text-gray-600">
              &copy; {new Date().getFullYear()} {companyInfo.name}. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
